import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download } from 'lucide-react';
import { buildCsv, downloadCsv, gradeLabel, todayStamp } from '@/lib/exportCsv';
import { useToast } from '@/hooks/use-toast';

// Free-sample (/try) leads. Supabase is the only place leads are kept: there's no automatic
// sync to Google Sheets or any other service, and this CSV export is the only way out.

interface LeadRow {
  id: string;
  created_at: string;
  first_name: string;
  email: string;
  email_type: 'work' | 'personal';
  role: string | null;
  school_name: string | null;
  year_group: string;
  status: string;
  grade: string | null;
  marketing_consent: boolean;
  marketing_consent_at: string | null;
  consent_text: string | null;
  converted_user_id: string | null;
  unsubscribed_at: string | null;
  source: string;
  utm_source: string | null;
  utm_campaign: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  class_teacher: 'Class teacher',
  english_lead: 'English lead',
  slt: 'SLT',
  other: 'Other',
};

type YesNo = '__all__' | 'yes' | 'no';

// Names and school names are typed by the public. Stop a spreadsheet treating them as formulas.
function safeCell(val: string | null | undefined): string {
  if (!val) return '';
  return /^[=+\-@\t\r]/.test(val) ? `'${val}` : val;
}

const yesNoMatch = (filter: YesNo, value: boolean) =>
  filter === '__all__' || (filter === 'yes') === value;

export default function AdminLeads() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [consentFilter, setConsentFilter] = useState<YesNo>('__all__');
  const [typeFilter, setTypeFilter] = useState<'__all__' | 'work' | 'personal'>('__all__');
  const [convertedFilter, setConvertedFilter] = useState<YesNo>('__all__');
  const [unsubscribedFilter, setUnsubscribedFilter] = useState<YesNo>('__all__');

  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['admin-leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sample_assessment_leads')
        .select('id, created_at, first_name, email, email_type, role, school_name, year_group, status, grade, marketing_consent, marketing_consent_at, consent_text, converted_user_id, unsubscribed_at, source, utm_source, utm_campaign')
        .order('created_at', { ascending: false })
        .limit(5000);
      if (error) throw error;
      return data as LeadRow[];
    },
  });

  const filtered = useMemo(() => leads.filter((l) =>
    yesNoMatch(consentFilter, l.marketing_consent) &&
    (typeFilter === '__all__' || l.email_type === typeFilter) &&
    yesNoMatch(convertedFilter, l.converted_user_id !== null) &&
    yesNoMatch(unsubscribedFilter, l.unsubscribed_at !== null)
  ), [leads, consentFilter, typeFilter, convertedFilter, unsubscribedFilter]);

  // Both presets always leave out anyone who has unsubscribed, whatever the filters say.
  const exportPreset = async (preset: 'consented' | 'work') => {
    const rows = leads.filter((l) =>
      l.unsubscribed_at === null &&
      (preset === 'consented' ? l.marketing_consent : l.email_type === 'work')
    );
    if (rows.length === 0) {
      toast({ title: 'Nothing to export', description: 'No leads match this list yet.' });
      return;
    }
    const csv = buildCsv(
      ['First name', 'Email', 'Email type', 'Role', 'School', 'Year group', 'Grade', 'Marketing consent', 'Consent given at', 'Consent wording', 'Signed up', 'Date', 'Source', 'UTM source', 'UTM campaign'],
      rows.map((l) => [
        safeCell(l.first_name),
        safeCell(l.email),
        l.email_type,
        l.role ? ROLE_LABELS[l.role] ?? l.role : '',
        safeCell(l.school_name),
        l.year_group,
        gradeLabel(l.grade),
        l.marketing_consent ? 'Yes' : 'No',
        l.marketing_consent_at ?? '',
        l.consent_text ?? '',
        l.converted_user_id ? 'Yes' : 'No',
        l.created_at.slice(0, 10),
        l.source,
        safeCell(l.utm_source),
        safeCell(l.utm_campaign),
      ]),
    );
    downloadCsv(`writeassess-leads-${preset === 'consented' ? 'consented' : 'work-addresses'}-${todayStamp()}.csv`, csv);
    if (user) {
      await supabase.rpc('log_admin_action', {
        p_action: 'leads_exported',
        p_target_type: 'leads',
        p_target_id: user.id,
        p_metadata: { preset, rows: rows.length },
      });
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const yesNoSelect = (value: YesNo, onChange: (v: YesNo) => void, label: string) => (
    <Select value={value} onValueChange={(v) => onChange(v as YesNo)}>
      <SelectTrigger className="w-44">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{label}: all</SelectItem>
        <SelectItem value="yes">{label}: yes</SelectItem>
        <SelectItem value="no">{label}: no</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            People who used the free sample assessment (/try). Stored only in Supabase; export below is the only way out.
          </p>
        </div>

        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-sm font-medium">Export (unsubscribed people are always left out)</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="space-y-1">
              <Button variant="outline" onClick={() => exportPreset('consented')} disabled={isLoading}>
                <Download className="mr-2 h-4 w-4" /> Consented
              </Button>
              <p className="text-xs text-muted-foreground max-w-xs">Ticked the marketing box.</p>
            </div>
            <div className="space-y-1">
              <Button variant="outline" onClick={() => exportPreset('work')} disabled={isLoading}>
                <Download className="mr-2 h-4 w-4" /> Work addresses
              </Button>
              <p className="text-xs text-muted-foreground max-w-xs">
                Legitimate-interest list: work addresses only, every email must include an unsubscribe link.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {yesNoSelect(consentFilter, setConsentFilter, 'Consent')}
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Email type: all</SelectItem>
              <SelectItem value="work">Email type: work</SelectItem>
              <SelectItem value="personal">Email type: personal</SelectItem>
            </SelectContent>
          </Select>
          {yesNoSelect(convertedFilter, setConvertedFilter, 'Converted')}
          {yesNoSelect(unsubscribedFilter, setUnsubscribedFilter, 'Unsubscribed')}
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">Could not load leads: {(error as Error).message}</p>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  {['Date', 'Name', 'Email', 'Type', 'Role', 'School', 'Year', 'Grade', 'Consent', 'Converted', 'Unsubscribed', 'Source'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-muted-foreground">No leads found.</td>
                  </tr>
                ) : filtered.map((l) => (
                  <tr key={l.id} className="border-b last:border-0">
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(l.created_at)}</td>
                    <td className="px-4 py-3">{l.first_name}</td>
                    <td className="px-4 py-3">{l.email}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{l.email_type}</Badge></td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.role ? ROLE_LABELS[l.role] ?? l.role : '—'}</td>
                    <td className="px-4 py-3">{l.school_name ?? '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.year_group}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {l.grade ?? (l.status === 'completed' ? '—' : <span className="text-muted-foreground">{l.status}</span>)}
                    </td>
                    <td className="px-4 py-3">{l.marketing_consent ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3">{l.converted_user_id ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.unsubscribed_at ? formatDate(l.unsubscribed_at) : '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {[l.source, l.utm_source, l.utm_campaign].filter(Boolean).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">{filtered.length} of {leads.length} lead{leads.length !== 1 ? 's' : ''}</p>
      </div>
    </DashboardLayout>
  );
}
